import { getInternalPathname } from '../lib/roleHistory';
import React, { useState, useEffect, createContext } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import DocumentAcknowledgementModal from './DocumentAcknowledgementModal';

// Context to share acknowledgement state
const AcknowledgementContext = createContext({
  hasPendingAcknowledgements: false,
  refreshAcknowledgements: () => {}
});

export const useAcknowledgements = () => React.useContext(AcknowledgementContext);

/**
 * AcknowledgementGate
 * 
 * This component wraps protected content and blocks access
 * if the user has pending document acknowledgements.
 * 
 * It shows the DocumentAcknowledgementModal which handles
 * both soft and hard acknowledgements.
 */
const AcknowledgementGate = ({ children }) => {
  const { user, token, loading: authLoading } = useAuth();
  const isAuthenticated = !!token && !!user;
  const [loading, setLoading] = useState(true);
  const [pendingAcknowledgements, setPendingAcknowledgements] = useState([]);
  const [showModal, setShowModal] = useState(false);

  // iter318 — System admins (super_admin / platform operator) and
  // tenant admins are exempt from user-facing document acknowledgement
  // modals (privacy policy, terms of service, refund policy, etc.).
  // Rationale: these staff/operator roles don't consume the tenant's
  // customer-facing legal contract — those documents govern the
  // parent/customer relationship. Popping the modal at every login
  // adds friction and testing noise without any legal or product value.
  const ADMIN_ROLES_EXEMPT_FROM_ACK = new Set(['admin', 'super_admin']);
  const isAdminExempt = ADMIN_ROLES_EXEMPT_FROM_ACK.has(user?.role);
  
  // Get current path to determine if we're on a protected route
  const currentPath = getInternalPathname();
  const publicPaths = ['/', '/login', '/register', '/reset-password', '/privacy-policy', '/terms-of-service', '/shipping-policy', '/return-refund-policy', '/do-not-sell-info'];
  const isPublicRoute = publicPaths.includes(currentPath) || currentPath.startsWith('/legal/');

  const checkPendingAcknowledgements = async () => {
    // Wait for auth to finish loading
    if (authLoading) {
      return;
    }
    
    // Don't check on public routes
    if (isPublicRoute) {
      setLoading(false);
      return;
    }
    
    if (!isAuthenticated || !user) {
      setLoading(false);
      return;
    }

    // Skip acknowledgement check for system + tenant admins.
    if (isAdminExempt) {
      setLoading(false);
      setPendingAcknowledgements([]);
      return;
    }

    try {
      const response = await api.get('/documents/pending-acknowledgements');
      const pending = response.data.pending || [];
      setPendingAcknowledgements(pending);
      
      // Show modal if there are any pending acknowledgements (and not dismissed this session)
      if (pending.length > 0) {
        const dismissed = sessionStorage.getItem('ack-dismissed');
        if (!dismissed) {
          setShowModal(true);
        }
      }
    } catch (error) {
      console.error('Failed to check pending acknowledgements:', error);
      // On error, don't block the user
      setPendingAcknowledgements([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only check when auth is done loading
    if (!authLoading) {
      checkPendingAcknowledgements();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, user?.id]);

  const handleComplete = () => {
    setShowModal(false);
    setPendingAcknowledgements([]);
  };

  const handleDismiss = () => {
    setShowModal(false);
    // Store dismissal so it re-shows next page load but doesn't block this session
    sessionStorage.setItem('ack-dismissed', Date.now().toString());
  };

  const refreshAcknowledgements = () => {
    checkPendingAcknowledgements();
  };

  // If auth is still loading, show loading state (only on protected routes)
  if (authLoading && !isPublicRoute) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  // If not authenticated or on public route, just render children
  if (!isAuthenticated || !user || isPublicRoute) {
    return children;
  }

  // Loading acknowledgements state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <AcknowledgementContext.Provider value={{ 
      hasPendingAcknowledgements: pendingAcknowledgements.length > 0,
      refreshAcknowledgements 
    }}>
      {/* Show modal if there are pending acknowledgements */}
      {showModal && pendingAcknowledgements.length > 0 && (
        <DocumentAcknowledgementModal onComplete={handleComplete} onDismiss={handleDismiss} />
      )}
      
      {/* Render children regardless - the modal blocks interaction */}
      {children}
    </AcknowledgementContext.Provider>
  );
};

export default AcknowledgementGate;
